import { motion } from 'framer-motion'
import { Award, Star, Zap, Rocket, Clock, Shield, Globe, Factory, Car, Shirt, Utensils } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { useTranslation } from '../utils/translations'
import { useState, useEffect } from 'react'

// Animations optimisées
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
}

// Logos des clients (remplacez par vos vraies images)
const clientLogos = [
  { id: 1, name: "Client 1", logo: process.env.PUBLIC_URL + "/images/clients/client1.webp" },
  { id: 2, name: "Client 2", logo: process.env.PUBLIC_URL + "/images/clients/client2.webp" },
  { id: 3, name: "Client 3", logo: process.env.PUBLIC_URL + "/images/clients/client3.webp" },
  { id: 4, name: "Client 4", logo: process.env.PUBLIC_URL + "/images/clients/client4.webp" },
  { id: 5, name: "Client 5", logo: process.env.PUBLIC_URL + "/images/clients/client5.webp" },
  { id: 6, name: "Client 6", logo: process.env.PUBLIC_URL + "/images/clients/client6.webp" },
  { id: 7, name: "Client 7", logo: process.env.PUBLIC_URL + "/images/clients/client7.webp" }
]

// Composant Carrousel de logos
const LogoCarousel = () => {
  const [currentSet, setCurrentSet] = useState(0)

  // Dupliquer les logos pour un défilement infini fluide
  const duplicatedLogos = [...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos]

  useEffect(() => {
    // Défilement automatique pour tous les appareils
    const interval = setInterval(() => {
      setCurrentSet(prev => (prev + 1) % 6)
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="relative overflow-hidden py-12">
      {/* Gradient overlays pour l'effet de fondu */}
      <div className="absolute left-0 top-0 w-20 md:w-32 h-full bg-gradient-to-r from-white to-transparent z-10"></div>
      <div className="absolute right-0 top-0 w-20 md:w-32 h-full bg-gradient-to-l from-white to-transparent z-10"></div>
      
      <motion.div
        className="flex space-x-6 md:space-x-16 items-center"
        animate={{ 
          x: `-${currentSet * (100 / 3)}%`
        }}
        transition={{ 
          duration: 1.5,
          ease: "easeInOut"
        }}
      >
        {duplicatedLogos.map((client, index) => (
          <motion.div
            key={`${client.id}-${index}`}
            className="flex-shrink-0"
            whileHover={{ scale: 1.1, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 w-32 h-28 md:w-48 md:h-40 flex items-center justify-center">
              <img
                src={client.logo}
                alt={client.name}
                className="max-w-full max-h-20 md:max-h-24 object-contain transition-all duration-300"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}

const About = () => {
  const { language } = useLanguage()
  const t = useTranslation(language)

  const stats = [
    { icon: Clock, value: '15+', labelKey: 'about.stat1', color: 'text-blue-600' },
    { icon: Rocket, value: '100+', labelKey: 'about.stat2', color: 'text-orange-600' },
    { icon: Globe, value: '3+', labelKey: 'about.stat3', color: 'text-green-600' },
    { icon: Shield, value: '98%', labelKey: 'about.stat4', color: 'text-purple-600' }
  ]

  const skills = [
    t('about.skill1'),
    t('about.skill2'),
    t('about.skill3'),
    t('about.skill4')
  ]

  // Données pour les images des secteurs d'expertise
const sectorImages = [
  {
    id: 1,
    image: "/images/auto1.webp",
    alt: t('about.sectorAutomotive')
  },
  {
    id: 2,
    image: "/images/auto2.webp",
    alt: t('about.sectorAutomotive')
  },
   {
    id: 4,
    image: "/images/acompeniement1.webp",
    alt: t('about.sectorTextil')
  },
  {
    id: 3,
    image: "/images/secteur3.webp",
    alt: t('about.sectorAgrofood')
  },
  {
    id: 4,
    image: "/images/auto4.webp",
    alt: t('about.sectorAutomotive')
  },
   {
    id: 4,
    image: "/images/acompeniement2.webp",
    alt: t('about.sectorTextil')
  }
]

  return (
    <div className="pt-26">
      {/* Hero Section avec image de fond */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600/90 via-blue-700/90 to-purple-600/90 text-white overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("images/about-hero.webp")',
            backgroundBlendMode: 'overlay'
          }}
        >
          {/* Overlay pour assombrir l'image et améliorer la lisibilité */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        {/* Pattern subtil */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeIn} className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
              <Star className="w-4 h-4 text-white mr-2" />
              <span className="text-sm font-medium text-white">{t('about.heroBadge')}</span>
            </motion.div>

            <motion.h1 variants={fadeIn} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('about.pageTitle')}
            </motion.h1>
            
            <motion.p variants={fadeIn} className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              {t('about.pageSubtitle')}
            </motion.p>
          </motion.div>

          {/* Stats Grid - Minimalist */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-5xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                whileHover={{ y: -5 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all"
              >
                <stat.icon className={`w-8 h-8 text-white mx-auto mb-3`} />
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-blue-100">{t(stat.labelKey)}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Vague décorative en bas */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-12">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" className="fill-white"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" className="fill-white"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* About Content Section - Modern & Clean */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/about7.webp"
                  alt={t('about.pageTitle')}
                  className="w-full h-[400px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="absolute bottom-6 left-6 bg-white rounded-xl p-2 shadow-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Award className="w-6 h-6 text-blue-600" />
                    <div>
                      <div className="font-bold text-gray-900">{t('about.expertTitle')}</div>
                      <div className="text-sm text-gray-600">{t('about.foundedBy')}</div>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              {/* Decorative Element */}
              <div className="absolute -z-10 top-8 -right-8 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-30"></div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  {t('about.expertiseTitle')}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
              </div>

              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  <span className="font-bold text-blue-600 text-xl">Texaconsult</span>&nbsp;
                  {t('about.expertiseText1')}
                </p>
                <p>{t('about.expertiseText2')}</p>
                <p>{t('about.expertiseText3')}</p>
              </div>

              {/* Skills Grid */}
              <div className="grid grid-cols-2 gap-4 pt-6">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5 }}
                    className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors"
                  >
                    <Zap className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">{skill}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clients Section - Modern Carousel */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('about.clientsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('about.clientsSubtitle')}
            </p>
          </motion.div>

          <LogoCarousel />
        </div>
      </section>

      {/* Section Images des Secteurs d'Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('about.sectorsTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('about.sectorsSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectorImages.map((sector, index) => (
              <motion.div
                key={sector.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="relative group"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                  <img
                    src={sector.image}
                    alt={sector.alt}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      {sector.alt}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              {t('about.missionTitle')}
            </h2>
            
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>{t('about.missionText1')}</p>
              <p className="font-semibold text-blue-600">{t('about.missionText2')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Image Expertise Additionnelle */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/about8.webp"
                alt="Notre expertise en action"
                className="w-full h-[400px] md:h-[500px] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Élément décoratif */}
            <div className="absolute -z-10 -bottom-8 -right-8 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute -z-10 -top-8 -left-8 w-64 h-64 bg-purple-100 rounded-full blur-3xl opacity-20"></div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Modern & Engaging */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              {t('about.ctaTitle')}
            </h2>
            
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              {t('about.ctaText')}
            </p>
            
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl"
            >
              <span>{t('about.ctaButton')}</span>
              <Rocket className="w-5 h-5 ml-2" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About